import React from 'react';
import './SignatureProgress.css';

const SignatureProgress = ({ document, showDetails = false }) => {
  if (!document) return null;
  
  // Determine document status
  const isSigned = document.status === 'signed';
  const isPartiallySigned = document.status === 'partially_signed';
  const isPending = document.status === 'pending';
  const isDraft = document.status === 'draft';
  
  // Get signature type
  const isSelfSign = document.signatureType === 'self';
  const isMeAndOthers = document.signatureType === 'me_and_others';
  const isOthersOnly = document.signatureType === 'others_only';
  
  // Calculate progress percentage
  const calculateProgress = () => {
    if (isSigned) return 100;
    if (isDraft) return 0;
    
    // For partially signed documents
    if (isPartiallySigned) {
      const totalSigners = document.recipients?.length || 0;
      const signedCount = document.signatures?.length || 0;
      
      // If me_and_others, add owner to total count
      const totalCount = isMeAndOthers ? totalSigners + 1 : totalSigners;
      const completedCount = signedCount;
      
      return Math.round((completedCount / totalCount) * 100);
    }
    
    return 0;
  };
  
  const progress = calculateProgress();
  
  // Get signers with status
  const getSignersWithStatus = () => {
    const signers = [];
    
    // Add owner if me_and_others
    if (isMeAndOthers) {
      const ownerSigned = document.ownerSigned || false;
      signers.push({
        name: 'You (Owner)',
        email: document.ownerEmail,
        status: ownerSigned ? 'signed' : 'pending',
        signedAt: document.ownerSignedAt || null
      });
    }
    
    // Add recipients
    if (document.recipients && document.recipients.length > 0) {
      document.recipients.forEach(recipient => {
        const hasSigned = document.signatures?.some(sig => 
          sig.signerEmail === recipient.email
        );
        
        signers.push({
          name: recipient.name,
          email: recipient.email,
          status: hasSigned ? 'signed' : 'pending',
          signedAt: hasSigned ? document.signatures.find(sig => 
            sig.signerEmail === recipient.email
          )?.signedAt : null
        });
      });
    }
    
    return signers;
  };
  
  const signers = getSignersWithStatus();
  
  // Generate colors for signers
  const getSignerColor = (index, status) => {
    if (status === 'signed') return '#4CAF50'; // Green for signed
    
    const colors = ['#2196F3', '#FF9800', '#9C27B0', '#F44336'];
    return colors[index % colors.length];
  };
  
  return (
    <div className="signature-progress">
      {/* Progress bar */}
      <div className="progress-bar-container">
        <div 
          className="progress-bar" 
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      
      {/* Progress text */}
      <div className="progress-text">
        {isSigned ? (
          <span className="status-complete">Completed</span>
        ) : isPartiallySigned ? (
          <span className="status-partial">{progress}% Complete</span>
        ) : isPending ? (
          <span className="status-pending">Waiting for signatures</span>
        ) : (
          <span className="status-draft">Draft</span>
        )}
      </div>
      
      {/* Signers avatars */}
      {(isPartiallySigned || isSigned) && (
        <div className="signers-avatars">
          {signers.map((signer, index) => (
            <div 
              key={signer.email}
              className={`signer-avatar ${signer.status === 'signed' ? 'signed' : 'pending'}`}
              style={{ 
                backgroundColor: getSignerColor(index, signer.status),
                zIndex: signers.length - index
              }}
              title={`${signer.name} (${signer.status === 'signed' ? 'Signed' : 'Pending'})`}
            >
              {signer.name.charAt(0).toUpperCase()}
            </div>
          ))}
        </div>
      )}
      
      {/* Detailed progress (optional) */}
      {showDetails && signers.length > 0 && (
        <div className="signers-details">
          {signers.map((signer, index) => (
            <div 
              key={signer.email}
              className={`signer-detail-item ${signer.status}`}
            >
              <div className="signer-info">
                <div className="signer-name">{signer.name}</div>
                <div className="signer-email">{signer.email}</div>
              </div>
              <div className="signer-status-indicator">
                {signer.status === 'signed' ? (
                  <>
                    <span className="status-icon">✓</span>
                    <span className="status-text">Signed</span>
                    {signer.signedAt && (
                      <span className="signed-date">
                        {new Date(signer.signedAt).toLocaleDateString()}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="status-icon">⌛</span>
                    <span className="status-text">Waiting</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SignatureProgress;
